import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UseFormReturn } from "react-hook-form";
import { Typography } from "@akuna/ui";
import type { ReservationFormValues } from "./reservationForm";
import { StayCoverageChoice } from "./StayCoverageChoice";
import { useReservationBillingOptions } from "@/hooks/useReservationBillingOptions";
import { useStayPlanStatus } from "@/hooks/useStayPlanStatus";
import { getHotelReservationNights } from "./stayReservationPersistence";
import { calculateReservationBilling } from "./reservationBilling";
import { StayPlanContractDialog } from "../plans/StayPlanContractDialog";
import { useContractStayPlan } from "../plans/useContractStayPlan";
import type { StayPlanContractDraft } from "../plans/stayPlanContractDraft";
import { useStayPlans } from "@/hooks/useStayPlans";
import { planDatesAhead } from "@/lib/daycare-hotel/planFixedDays";
import { PlanDaysCard } from "./PlanDaysCard";
import { PetPlanDaysSummary } from "./PetPlanDaysSummary";

export function PlanAndDaysSection({
  form,
  stayDates,
  hasWeeklyPlan = false,
}: {
  form: UseFormReturn<ReservationFormValues>;
  stayDates: string[];
  /** Pet com plano semanal: a data decide a cobertura, então a lista some. */
  hasWeeklyPlan?: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const [contractOpen, setContractOpen] = useState(false);
  const [contractKindFilter, setContractKindFilter] = useState<"recurring" | "day_package">("recurring");
  const petId = form.watch("petId") || null;
  const ownerId = form.watch("ownerId") || null;
  const checkInDate = form.watch("checkInDate");
  const checkOutDate = form.watch("checkOutDate");
  const moduleType = form.watch("moduleType");
  const planChoice = form.watch("planChoice") ?? null;
  const selectedContractId = form.watch("stayPlanContractId");
  const selectedPlanContractId = planChoice === "existing" ? selectedContractId : null;
  const hasSelectedPlan = planChoice === "existing" && selectedPlanContractId !== null;
  const module = moduleType === "hotel" ? "hotel" : "daycare";
  const units = moduleType === "hotel" ? Math.max(1, getHotelReservationNights({ moduleType, checkInDate, checkOutDate })) : Math.max(1, stayDates.length);
  const planDraft = form.watch("planDraft") ?? null;
  const plansForDraft = useStayPlans(module).data;
  const draftPlan = planDraft ? (plansForDraft ?? []).find((plan) => plan.id === planDraft.planId) ?? null : null;
  const options = useReservationBillingOptions({ module, petId, checkInDate });
  const assigned = options.assignedContracts.find((item) => item.contractId === selectedContractId);
  const currentCoverage = planChoice === "avulso"
    ? { kind: "avulso" as const, contractId: null, name: t("planChoice.payAsYouGoOption") }
    : planChoice === "existing" && assigned
      ? {
          kind: assigned.kind === "day_package" ? "package" as const : "plan" as const,
          contractId: assigned.contractId,
          name: assigned.name,
        }
      : null;
  const { status: planStatus } = useStayPlanStatus({ module, petId, date: checkInDate, contractId: selectedPlanContractId });
  const contractPlan = useContractStayPlan();
  const weeklyReservationUnits = (() => {
    const week = planStatus?.weeklyQuota?.week;
    if (!week || moduleType !== "day_care") return units;
    return stayDates.filter((date) => date >= week.start && date <= week.end).length;
  })();

  useEffect(() => {
    if (planDraft || hasWeeklyPlan) return;
    if (options.isLoading || options.isFetching || options.isError) return;

    if (planChoice === null && !selectedContractId && options.assignedContracts.length === 1) {
      const defaultPlan = options.assignedContracts[0];
      form.setValue("planChoice", "existing", { shouldValidate: true });
      form.setValue("stayPlanContractId", defaultPlan.contractId, { shouldValidate: true });
      form.setValue("stayPlanUnitsToUse", Math.max(1, Math.min(defaultPlan.remainingDays ?? units, units)), { shouldValidate: true });
      form.setValue("stayPlanName", defaultPlan.name, { shouldValidate: true });
      form.setValue("billing", "plan", { shouldValidate: true });
      return;
    }

    if (planChoice === "existing" && selectedContractId && !assigned) {
      form.setValue("stayPlanContractId", null, { shouldValidate: true });
      form.setValue("stayPlanUnitsToUse", null, { shouldValidate: true });
      form.setValue("stayPlanName", null, { shouldValidate: true });
    }
  }, [assigned, form, options.assignedContracts, options.isError, options.isFetching, options.isLoading, planChoice, planDraft, hasWeeklyPlan, selectedContractId, units]);

  const billing = calculateReservationBilling({
    moduleType,
    requestedDays: units,
    dailyRate: options.dailyRates?.[moduleType === "hotel" ? "hotelNightlyRate" : "daycareDailyRate"] ?? null,
    plan: planChoice === "existing" && assigned
      ? { origin: "existing", availableDays: assigned.remainingDays ?? units, price: assigned.price }
      : null,
  });

  const handleContract = async (draft: StayPlanContractDraft) => {
    if (!petId) return;
    const isWeeklyDraft = draft.module === "daycare" && (draft.fixedWeekdays?.length ?? 0) > 0;
    if (isWeeklyDraft) {
      // O plano é a reserva: nada é gravado até "Criar reserva".
      const plan = (plansForDraft ?? []).find((item) => item.id === draft.planId);
      setContractOpen(false);
      form.setValue("planDraft", draft, { shouldValidate: true });
      form.setValue("planChoice", null, { shouldValidate: true });
      form.setValue("stayPlanContractId", null, { shouldValidate: true });
      form.setValue("stayPlanUnitsToUse", null, { shouldValidate: true });
      form.setValue("billing", "plan", { shouldValidate: true });
      form.setValue("checkInDate", draft.startsOn, { shouldValidate: true });
      form.setValue("dateMode", "single", { shouldValidate: true });
      form.setValue("recurrence", null, { shouldValidate: true });
      const chargeNow = draft.payNow && draft.billingTiming !== "end";
      form.setValue("stayPlanChargeInComanda", chargeNow, { shouldValidate: true });
      if (chargeNow && plan) {
        form.setValue("comanda", [
          ...(form.getValues("comanda") ?? []),
          { itemId: null, itemName: `${t("planContract.membershipLine")}: ${plan.name}`, quantity: 1, unitPrice: plan.price, billingRole: "extra" },
        ], { shouldValidate: true });
      }
      return;
    }
    const { contractId, planName, planPrice } = await contractPlan.mutateAsync({ draft, petId, ownerId, deferInitialCharge: true });
    setContractOpen(false);
    form.setValue("planChoice", "existing", { shouldValidate: true });
    form.setValue("stayPlanContractId", contractId, { shouldValidate: true });
    form.setValue("stayPlanUnitsToUse", Math.max(1, units), { shouldValidate: true });
    form.setValue("billing", "plan", { shouldValidate: true });
    if (draft.payNow && draft.billingTiming !== "end") {
      form.setValue("stayPlanChargeInComanda", true, { shouldValidate: true });
      const currentLines = form.getValues("comanda") ?? [];
      form.setValue("comanda", [
        ...currentLines,
        {
          itemId: null,
          itemName: `${t("planContract.membershipLine")}: ${planName}`,
          quantity: 1,
          unitPrice: planPrice,
          billingRole: "extra",
        },
      ], { shouldValidate: true });
    }
  };

  const undoDraft = () => {
    const membership = draftPlan ? `${t("planContract.membershipLine")}: ${draftPlan.name}` : null;
    form.setValue("planDraft", null, { shouldValidate: true });
    form.setValue("stayPlanChargeInComanda", false, { shouldValidate: true });
    form.setValue("billing", "avulso", { shouldValidate: true });
    form.setValue("comanda", (form.getValues("comanda") ?? []).filter((line) => line.itemName !== membership), { shouldValidate: true });
  };

  return (
    <section className="grid gap-2 bg-card py-1">
      <Typography variant="h5">{t("planAndDays")}</Typography>
      {planDraft ? (
        <>
          <PlanDaysCard
            name={draftPlan?.name ?? ""}
            weekdays={planDraft.fixedWeekdays ?? []}
            dates={planDatesAhead({
              anchor: planDraft.startsOn,
              from: planDraft.startsOn,
              weekdays: planDraft.fixedWeekdays ?? [],
              weekInterval: draftPlan?.weekInterval ?? 1,
              startsOn: planDraft.startsOn,
            })}
            weekInterval={draftPlan?.weekInterval ?? 1}
            badge="planDays.draftBadge"
            onUndo={undoDraft}
          />
          <p className="text-xs text-muted-foreground">{t("planDays.draftHint")}</p>
        </>
      ) : (
      <>
      {moduleType === "day_care" ? <PetPlanDaysSummary petId={petId} /> : null}
      {hasWeeklyPlan ? null : (
      <StayCoverageChoice
        choice={planChoice}
        petId={petId}
        isLoading={options.isLoading}
        isError={options.isError}
        assignedContracts={options.assignedContracts}
        selectedAssignedId={selectedPlanContractId}
        currentCoverage={currentCoverage}
        alerts={hasSelectedPlan ? planStatus?.alerts ?? [] : []}
        usageSummary={hasSelectedPlan ? planStatus?.usageSummary ?? null : null}
        reservationUnits={units}
        weeklyReservationUnits={weeklyReservationUnits}
        onChange={({ choice, assignedId }) => {
          const nextAssigned = options.assignedContracts.find((item) => item.contractId === assignedId);
          const availableUnits = nextAssigned?.remainingDays ?? units;
          const unitsToUse = Math.max(1, Math.min(availableUnits, units));

          form.setValue("planChoice", choice, { shouldValidate: true });
          form.setValue("stayPlanContractId", assignedId, { shouldValidate: true });
          form.setValue("stayPlanUnitsToUse", choice === "avulso" || choice === null ? null : unitsToUse, { shouldValidate: true });
          form.setValue("stayPlanName", nextAssigned?.name ?? null, { shouldValidate: true });
          form.setValue("billing", choice === "avulso" ? "avulso" : "plan", { shouldValidate: true });
        }}
        onContract={(kindFilter) => {
          setContractKindFilter(kindFilter);
          setContractOpen(true);
        }}
      />
      )}
      </>
      )}

      {contractOpen && petId ? (
        <StayPlanContractDialog
          open={contractOpen}
          onOpenChange={setContractOpen}
          module={module}
          availableModules={[module]}
          kindFilter={contractKindFilter}
          defaultStartsOn={checkInDate}
          submitting={contractPlan.isPending}
          onSubmit={handleContract}
        />
      ) : null}
    </section>
  );
}
